import React, { useState, useEffect } from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/Footer';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import SEO from 'components/SEO';
import { sendRequirementsFormEmail } from 'utils/emailService';
import { useLocation, useNavigate } from 'react-router-dom';

const Services = () => {
  const [isCalendlyLoaded, setIsCalendlyLoaded] = useState(false);
  const [calendlyError, setCalendlyError] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const location = useLocation();

  // Enhanced SEO Data
  const seoData = {
    title: 'Our Services - Web & Mobile App Development | AI Solutions | OctalChip',
    description: 'Comprehensive digital solutions including web development, mobile app development, AI-powered solutions, cloud services, and DevOps. Transform your business with our expert development services.',
    keywords: 'web development services, mobile app development, AI development, cloud services, DevOps, UI/UX design, custom software development, web applications, mobile applications, artificial intelligence, machine learning, AWS, Azure, GCP, React, Node.js, Python, JavaScript, TypeScript, agile development, software consulting, enterprise software, SaaS development, e-commerce development, API development, database design, frontend development, backend development, full-stack development, responsive design, progressive web apps, native apps, cross-platform development, Flutter, React Native, iOS development, Android development, cloud computing, infrastructure as code, containerization, microservices, serverless architecture',
    canonical: 'https://www.octalchip.com/services',
    tags: ['web development services', 'mobile app development', 'AI development', 'cloud services', 'DevOps'],
    additionalMeta: [
      { name: 'theme-color', content: '#00d690' },
      { name: 'mobile-web-app-capable', content: 'yes' }
    ],
    breadcrumbs: [
      { name: 'Home', url: 'https://www.octalchip.com/' },
      { name: 'Services', url: 'https://www.octalchip.com/services' }
    ],

    // GEO-specific data
    geoData: {
      region: "IN-GJ",
      placename: "Gujarat, India",
      latitude: "23.0225",
      longitude: "72.5714"
    },
    localBusinessData: true,
    serviceArea: [
      { "@type": "Country", "name": "India" },
      { "@type": "Country", "name": "Germany" },
      { "@type": "Country", "name": "Italy" },
      { "@type": "Country", "name": "Dubai" },
      { "@type": "Country", "name": "United Kingdom" },
      { "@type": "Country", "name": "Netherlands" }
    ],

    // AEO-specific data
    faqData: [
      {
        question: 'What development services does OctalChip offer?',
        answer: 'We offer comprehensive web development, mobile app development, AI integration, cloud & DevOps, UI/UX design, and backend development services tailored to your business needs.'
      },
      {
        question: 'How much do your development services cost?',
        answer: 'Our pricing starts at $18/hour for hourly engagement, $2560/month for dedicated developers, and custom pricing for fixed-cost projects. Contact us for a detailed quote.'
      },
      {
        question: 'What technologies do you use for development?',
        answer: 'We use modern technologies including React, Node.js, Python, JavaScript, TypeScript, AWS, Azure, GCP, Docker, Kubernetes, and various AI/ML frameworks.'
      },
      {
        question: 'How long does a typical project take?',
        answer: 'Project timelines vary based on complexity. Simple web applications take 2-4 weeks, while complex enterprise solutions can take 3-6 months. We provide detailed timelines during consultation.'
      },
      {
        question: 'Do you provide maintenance and support?',
        answer: 'Yes, we offer comprehensive post-launch support including maintenance, updates, bug fixes, and feature enhancements to ensure your application continues to perform optimally.'
      },
      {
        question: 'Can you work with existing systems?',
        answer: 'Absolutely! We specialize in integrating with existing systems and can help modernize legacy applications while maintaining data integrity and business continuity.'
      }
    ],

    // Voice search optimization
    voiceSearchKeywords: [
      "web development services",
      "mobile app development company",
      "AI development solutions",
      "cloud services provider",
      "DevOps consulting services",
      "custom software development",
      "UI UX design services",
      "digital transformation services"
    ],
    conversationalQueries: [
      "What are the best web development services?",
      "How much does mobile app development cost?",
      "Who provides AI development services?",
      "What is DevOps and why do I need it?",
      "How to choose a software development company?"
    ],

    // Featured snippet optimization
    featuredSnippetContent: "OctalChip offers comprehensive web development, mobile app development, AI-powered solutions, cloud services, and DevOps services. Our pricing starts at $18/hour with 25+ experienced developers.",
    
    // How-to data for AEO
    howToData: [
      {
        name: "How to Choose a Web Development Company",
        description: "A comprehensive guide to selecting the right web development partner for your business",
        totalTime: "PT10M",
        steps: [
          {
            name: "Define Your Requirements",
            text: "Clearly outline your project goals, features, timeline, and budget requirements."
          },
          {
            name: "Research Companies",
            text: "Look for companies with relevant experience, positive reviews, and a strong portfolio."
          },
          {
            name: "Check Technical Expertise",
            text: "Verify their technical skills in the technologies you need for your project."
          },
          {
            name: "Evaluate Communication",
            text: "Ensure they have clear communication processes and regular update mechanisms."
          },
          {
            name: "Review Pricing and Contracts",
            text: "Compare pricing models and ensure contract terms are clear and fair."
          }
        ]
      }
    ],

    // List data for featured snippets
    listData: {
      name: "OctalChip Service Offerings",
      description: "Complete list of development services offered by OctalChip",
      items: [
        {
          name: "Web Development",
          description: "Custom web applications built with React, Next.js, Vue.js, and Node.js. Responsive, scalable, and SEO-optimized websites",
          url: "https://www.octalchip.com/services/web-development"
        },
        {
          name: "Mobile App Development",
          description: "Native and cross-platform mobile applications for iOS and Android using React Native, Flutter, and native development",
          url: "https://www.octalchip.com/services/mobile-app-development"
        },
        {
          name: "AI Integration",
          description: "AI-powered solutions including machine learning models, chatbots, predictive analytics, and intelligent automation",
          url: "https://www.octalchip.com/services/ai-integration"
        },
        {
          name: "Cloud & DevOps",
          description: "Scalable cloud infrastructure setup, migration, and management with AWS, Azure, GCP, CI/CD pipelines, and monitoring",
          url: "https://www.octalchip.com/services/cloud-devops"
        },
        {
          name: "UI/UX Design",
          description: "User-centered design with user research, wireframing, prototyping, design systems, and usability testing",
          url: "https://www.octalchip.com/services/ui-ux-design"
        },
        {
          name: "Backend Development",
          description: "Robust backend systems with microservices architecture, APIs, database optimization, and real-time communication",
          url: "https://www.octalchip.com/services/backend-development"
        }
      ]
    },

    // Table data for pricing featured snippets
    tableData: {
      name: "OctalChip Service Pricing",
      description: "Detailed pricing information for all development services",
      about: "Development Services Pricing Structure",
      table: {
        "@type": "Table",
        "name": "Service Pricing Structure",
        "about": "Pricing options for different development services",
        "table": [
          ["Service Type", "Pricing Model", "Starting Price", "Best For"],
          ["Hourly Engagement", "Pay-per-hour", "$18/hour", "Small projects, MVPs"],
          ["Monthly Dedicated", "Monthly retainer", "$2560/month", "Ongoing development"],
          ["Fixed Cost", "Project-based", "Custom quote", "Complete solutions"],
          ["AI Development", "Hourly/Project", "$25/hour", "AI integration projects"],
          ["Cloud Services", "Hourly/Project", "$20/hour", "Infrastructure setup"]
        ]
      }
    },
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Web & Mobile App Development Services",
      "description": "Comprehensive digital solutions including web development, mobile app development, AI-powered solutions, cloud services, and DevOps.",
      "url": "https://www.octalchip.com/services",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip",
        "url": "https://www.octalchip.com",
        "logo": "https://www.octalchip.com/favicon.svg",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "404, Opera point, kothariya",
          "addressLocality": "Gujarat",
          "addressCountry": "IN",
          "postalCode": "360022"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "sales",
          "email": "business@octalchip.com",
          "telephone": "+91-7574082582"
        }
      },
      "serviceType": "Software Development",
      "areaServed": {
        "@type": "Country",
        "name": "India"
      },
      "offers": [
        {
          "@type": "Offer",
          "name": "Hourly Engagement",
          "description": "Pay-as-you-go development services",
          "price": "18",
          "priceCurrency": "USD",
          "priceSpecification": {
            "@type": "UnitPriceSpecification",
            "price": "18",
            "priceCurrency": "USD",
            "unitText": "per hour"
          }
        },
        {
          "@type": "Offer",
          "name": "Monthly Dedicated Developer",
          "description": "Full-time developer dedicated to your project",
          "price": "2560",
          "priceCurrency": "USD",
          "priceSpecification": {
            "@type": "UnitPriceSpecification",
            "price": "2560",
            "priceCurrency": "USD",
            "unitText": "per month"
          }
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Development Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Web Development",
              "description": "Custom web applications built with React, Next.js, Vue.js, and Node.js. Responsive, scalable, and SEO-optimized websites that drive business growth"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Mobile App Development",
              "description": "Native and cross-platform mobile applications for iOS and Android using React Native, Flutter, and native development for optimal performance"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "AI Integration",
              "description": "AI-powered solutions including machine learning models, chatbots, predictive analytics, and intelligent automation to transform your business"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Cloud & DevOps",
              "description": "Scalable cloud infrastructure setup, migration, and management with AWS, Azure, GCP, automated CI/CD pipelines, and monitoring solutions"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "UI/UX Design",
              "description": "User-centered design with user research, wireframing, prototyping, design systems, and usability testing for exceptional user experiences"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Backend Development",
              "description": "Robust backend systems with microservices architecture, APIs, database optimization, and real-time communication for scalable applications"
            }
          }
        ]
      }
    }
  };

  useEffect(() => {
    // Check if Calendly is already loaded
    if (window.Calendly) {
      setIsCalendlyLoaded(true);
      return;
    }

    // Load Calendly script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => {
      setIsCalendlyLoaded(true);
      setCalendlyError(false); // Clear any previous error state
    };
    script.onerror = () => {
      console.error('Failed to load Calendly script');
      setCalendlyError(true);
    };
    
    document.head.appendChild(script);

    // Set a timeout to show error if Calendly doesn't load within 15 seconds
    const timeoutId = setTimeout(() => {
      if (!window.Calendly) {
        console.warn('Calendly script took too long to load');
        setCalendlyError(true);
      }
    }, 15000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  // Additional effect to ensure Calendly widget initializes properly
  useEffect(() => {
    if (isCalendlyLoaded && window.Calendly) {
      // Wait a bit for the DOM to be ready, then manually initialize the widget
      setTimeout(() => {
        const widget = document.querySelector('.calendly-inline-widget');
        if (widget && !widget.hasAttribute('data-processed')) {
          try {
            window.Calendly.initInlineWidget({
              url: 'https://calendly.com/business-octalchip/30min',
              parentElement: widget
            });
          } catch (error) {
            console.error('Error initializing Calendly widget:', error);
          }
        }
      }, 500);
    }
  }, [isCalendlyLoaded]);

  // Handle scroll to section when page loads with hash
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          // Calculate header height (top bar + main nav bar)
          const headerHeight = 100; // Approximate height of both header bars
          const elementPosition = element.offsetTop - headerHeight;
          
          window.scrollTo({
            top: elementPosition,
            behavior: 'smooth'
          });
        }, 100);
      }
    }
  }, [location.hash]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await sendRequirementsFormEmail(formData);
      
      if (result.success) {
        setSubmitMessage('Thank you! Your requirements have been submitted successfully. We\'ll get back to you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: '',
          requirements: ''
        });
      } else {
        setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    {
      title: 'Web Development',
      description: 'Transform your digital presence with custom web applications built using cutting-edge technologies. We create responsive, scalable, and high-performance websites that drive business growth.',
      icon: 'Globe',
      features: ['Responsive Design', 'SEO Optimization', 'Performance Optimization', 'Cross-browser Compatibility', 'Scalable Architecture', 'Security First'],
      technologies: ['React', 'Next.js', 'Vue.js', 'Node.js', 'TypeScript', 'Angular', 'Express.js', 'MongoDB', 'PostgreSQL'],
      projects: '25+',
      timeline: '2-8 weeks',
      path: '/services/web-development',
      useCases: ['E-commerce Platforms', 'SaaS Applications', 'Corporate Websites', 'Web Portals', 'Progressive Web Apps', 'Dashboard & Analytics']
    },
    {
      title: 'Mobile App Development',
      description: 'Create powerful mobile applications for iOS and Android. We build native and cross-platform apps that deliver exceptional user experiences and drive business growth.',
      icon: 'Smartphone',
      features: ['Cross-platform Development', 'Native Performance', 'App Store Optimization', 'Push Notifications', 'Offline Functionality', 'Security & Privacy'],
      technologies: ['React Native', 'Flutter', 'iOS (Swift)', 'Android (Kotlin)', 'Firebase', 'Xamarin', 'Ionic', 'App Store Connect', 'Google Play Console'],
      projects: '15+',
      timeline: '4-12 weeks',
      path: '/services/mobile-app-development',
      useCases: ['E-commerce Mobile Apps', 'Social Media Apps', 'Healthcare Apps', 'Fitness & Wellness', 'Food Delivery Apps', 'Finance & Banking']
    },
    {
      title: 'AI Integration',
      description: 'Harness the power of artificial intelligence to transform your business. We build intelligent solutions that automate processes, enhance decision-making, and deliver exceptional user experiences.',
      icon: 'Cpu',
      features: ['Machine Learning Models', 'Intelligent Chatbots', 'Predictive Analytics', 'Process Automation', 'Computer Vision', 'Natural Language Processing'],
      technologies: ['Python', 'TensorFlow', 'PyTorch', 'OpenAI API', 'Scikit-learn', 'Pandas', 'Keras', 'Hugging Face', 'OpenCV'],
      projects: '10+',
      timeline: '3-8 weeks',
      path: '/services/ai-integration',
      useCases: ['Customer Support Chatbots', 'Recommendation Systems', 'Fraud Detection', 'Predictive Maintenance', 'Image Recognition', 'Sentiment Analysis']
    },
    {
      title: 'Cloud & DevOps',
      description: 'Transform your infrastructure with scalable cloud solutions and automated DevOps practices. We help you build, deploy, and manage applications with speed, reliability, and efficiency.',
      icon: 'Cloud',
      features: ['Cloud Migration', 'CI/CD Pipelines', 'Infrastructure as Code', 'Container Orchestration', 'Monitoring & Logging', 'Security & Compliance'],
      technologies: ['AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes', 'Terraform', 'Jenkins', 'GitLab CI', 'Ansible'],
      projects: '20+',
      timeline: '2-6 weeks',
      path: '/services/cloud-devops',
      useCases: ['Cloud Migration', 'Microservices Architecture', 'Auto-scaling Solutions', 'Disaster Recovery', 'Multi-cloud Strategy', 'DevSecOps']
    },
    {
      title: 'UI/UX Design',
      description: 'Create exceptional user experiences that delight users and drive business results. We combine user research, thoughtful design, and testing to build interfaces that work beautifully.',
      icon: 'LayoutTemplate',
      features: ['User Research', 'Wireframing', 'Prototyping', 'Design Systems', 'Usability Testing', 'Visual Design'],
      technologies: ['Figma', 'Adobe XD', 'Sketch', 'InVision', 'Principle', 'Framer', 'Adobe Illustrator', 'Adobe Photoshop', 'Miro'],
      projects: '30+',
      timeline: '1-4 weeks',
      path: '/services/ui-ux-design',
      useCases: ['Web Application Design', 'Mobile App Design', 'Dashboard Design', 'E-commerce Design', 'SaaS Platform Design', 'Brand Identity']
    },
    {
      title: 'Backend Development',
      description: 'Build robust, scalable backend systems that power your applications. We create high-performance APIs, microservices, and databases that handle millions of requests with reliability and speed.',
      icon: 'Server',
      features: ['API Development', 'Database Design', 'Microservices Architecture', 'Performance Optimization', 'Security & Authentication', 'Real-time Communication'],
      technologies: ['Node.js', 'Python', 'PostgreSQL', 'MongoDB', 'Redis', 'Express.js', 'Django', 'GraphQL', 'Docker'],
      projects: '35+',
      timeline: '3-10 weeks',
      path: '/services/backend-development',
      useCases: ['RESTful APIs', 'Microservices', 'Real-time Systems', 'E-commerce Backend', 'SaaS Platforms', 'Data Processing']
    }
  ];

  const pricingPlans = [
    {
      title: 'HOURLY ENGAGEMENT',
      price: '$18',
      period: 'Per Hour',
      description: 'Pay-as-you-go. Perfect for short tasks or MVPs.',
      features: [
        '3–5 Yrs Experienced Developers',
        'Daily Progress Updates',
        'Ideal for Quick Turnarounds'
      ],
      popular: false
    },
    {
      title: 'MONTHLY DEDICATED DEV',
      price: '$2560',
      period: 'Per Month (160 hrs)',
      description: 'Hire a full-time expert dedicated to your project.',
      features: [
        'Full Stack Developer',
        'Team Collaboration',
        'Priority Support'
      ],
      popular: true
    },
    {
      title: 'FIXED-COST PROJECTS',
      price: '$$$$',
      period: 'Custom Pricing',
      description: 'Get your full solution delivered within a timeline.',
      features: [
        'Detailed Estimation & Scope',
        'Agile Sprint Milestones',
        'One-Time Payment'
      ],
      popular: false
    }
  ];

  const developmentProcessSteps = [
    {
      phase: 'Discovery & Analysis',
      duration: '1-2 weeks',
      description: 'Understanding your business requirements, target audience, and technical constraints',
      deliverables: ['Requirements Document', 'Technical Architecture', 'Project Timeline'],
      icon: <Icon name="Search" size={24} className="text-blue-600" />
    },
    {
      phase: 'Design & Prototyping',
      duration: '2-3 weeks',
      description: 'Creating wireframes, mockups, and interactive prototypes for user validation',
      deliverables: ['Wireframes', 'UI/UX Design', 'Interactive Prototype'],
      icon: <Icon name="Palette" size={24} className="text-purple-600" />
    },
    {
      phase: 'Development & Testing',
      duration: '4-8 weeks',
      description: 'Building your solution with continuous testing and regular progress updates',
      deliverables: ['MVP Development', 'Quality Assurance', 'Code Reviews'],
      icon: <Icon name="Settings" size={24} className="text-green-600" />
    },
    {
      phase: 'Deployment & Launch',
      duration: '1 week',
      description: 'Deploying to production, monitoring performance, and providing launch support',
      deliverables: ['Production Deployment', 'Performance Monitoring', 'Launch Support'],
      icon: <Icon name="Rocket" size={24} className="text-orange-600" />
    },
    {
      phase: 'Support & Maintenance',
      duration: 'Ongoing',
      description: 'Providing ongoing support, updates, and feature enhancements',
      deliverables: ['Bug Fixes', 'Feature Updates', 'Performance Optimization'],
      icon: <Icon name="Wrench" size={24} className="text-gray-600" />
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        ogImage={seoData.ogImage}
        tags={seoData.tags}
        additionalMeta={seoData.additionalMeta}
        breadcrumbs={seoData.breadcrumbs}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
        geoData={seoData.geoData}
        localBusinessData={seoData.localBusinessData}
        serviceArea={seoData.serviceArea}
        voiceSearchKeywords={seoData.voiceSearchKeywords}
        conversationalQueries={seoData.conversationalQueries}
        featuredSnippetContent={seoData.featuredSnippetContent}
        howToData={seoData.howToData}
        listData={seoData.listData}
        tableData={seoData.tableData}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-gray-800 mb-6 animate-fade-in-up">
              Our Services
            </h1>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
              Comprehensive digital solutions tailored to your business needs. From design to deployment, 
              we deliver innovative web, mobile, and cloud solutions that drive growth and success.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">What We Offer</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive digital solutions designed to transform your business and drive success
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover-lift group relative overflow-hidden cursor-pointer"
                style={{ borderRadius: '12px' }}
                onClick={() => navigate(service.path)}
              >
                {/* Gradient overlay on hover */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"
                  style={{
                    background: 'linear-gradient(45deg, #00dc88, #00bcd4)',
                    borderRadius: '14px',
                    top: '-2px',
                    left: '-2px',
                    right: '-2px',
                    bottom: '-2px',
                    zIndex: '-1'
                  }}
                ></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-white/20 transition-all duration-300">
                    <Icon 
                      name={service.icon} 
                      size={32} 
                      className="text-[#00d690] group-hover:text-white transition-all duration-300" 
                    />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 group-hover:text-white mb-4 transition-all duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 group-hover:text-white/90 leading-relaxed transition-all duration-300 mb-4">
                    {service.description}
                  </p>
                  
                  {/* Service Stats */}
                  <div className="flex justify-between items-center mb-4 text-sm">
                    <span className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
                      {service.projects} projects
                    </span>
                    <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
                      {service.timeline}
                    </span>
                  </div>
                  
                  {/* Key Features */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 group-hover:text-white/90 mb-2 transition-all duration-300">
                      Key Features:
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <span key={idx} className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
                          {feature}
                        </span>
                      ))}
                      {service.features.length > 3 && (
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
                          +{service.features.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Technologies */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 group-hover:text-white/90 mb-2 transition-all duration-300">
                      Technologies:
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {service.technologies.slice(0, 4).map((tech, idx) => (
                        <span key={idx} className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded text-xs group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
                          {tech}
                        </span>
                      ))}
                      {service.technologies.length > 4 && (
                        <span className="bg-[#00d690]/10 text-[#00d690] px-2 py-1 rounded text-xs group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
                          +{service.technologies.length - 4} more
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <button 
                    className="w-full bg-[#00d690] hover:bg-[#00b876] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 group-hover:bg-white group-hover:text-[#00d690]"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(service.path);
                    }}
                  >
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI/ML Services Cross-Link Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-[#00d690]/10 via-white to-[#00d690]/5">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-[#00d690]/20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="w-16 h-16 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-xl flex items-center justify-center mb-6">
                    <Icon name="Brain" size={32} className="text-white" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                    Explore Our AI & ML Services
                  </h2>
                  <div className="w-24 h-1 bg-[#00d690] mb-6"></div>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    Looking for specialized AI and Machine Learning solutions? We offer comprehensive AI/ML services 
                    including Machine Learning Development, Natural Language Processing, Computer Vision, Predictive Analytics, 
                    AI Chatbots, Deep Learning, Data Science, and AI Consulting.
                  </p>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Transform your business with cutting-edge AI technologies that automate processes, enhance decision-making, 
                    and deliver intelligent solutions tailored to your needs.
                  </p>
                  <div className="flex flex-wrap gap-3 mb-6">
                    <span className="bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full text-sm font-medium">Machine Learning</span>
                    <span className="bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full text-sm font-medium">NLP</span>
                    <span className="bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full text-sm font-medium">Computer Vision</span>
                    <span className="bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full text-sm font-medium">Predictive Analytics</span>
                    <span className="bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full text-sm font-medium">AI Chatbots</span>
                    <span className="bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full text-sm font-medium">Deep Learning</span>
                  </div>
                  <Button
                    variant="primary"
                    onClick={() => navigate('/ai-ml-services')}
                    className="px-8 py-3 text-lg group relative overflow-hidden transform transition-all duration-300 hover:scale-110 hover:shadow-2xl hover:shadow-[#00d690]/50 active:scale-105"
                    iconName="ChevronRight"
                    iconPosition="right"
                    size="lg"
                  >
                    View AI & ML Services
                  </Button>
                </div>
                <div className="relative">
                  <div className="bg-gradient-to-br from-[#00d690]/20 to-[#00b876]/20 rounded-2xl p-8 h-full flex flex-col justify-center">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                        <div className="text-3xl font-bold text-[#00d690] mb-2">9</div>
                        <div className="text-sm text-gray-600">AI/ML Services</div>
                      </div>
                      <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                        <div className="text-3xl font-bold text-[#00d690] mb-2">100+</div>
                        <div className="text-sm text-gray-600">AI Projects</div>
                      </div>
                      <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                        <div className="text-3xl font-bold text-[#00d690] mb-2">95%+</div>
                        <div className="text-sm text-gray-600">Model Accuracy</div>
                      </div>
                      <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                        <div className="text-3xl font-bold text-[#00d690] mb-2">24/7</div>
                        <div className="text-sm text-gray-600">AI Support</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Development Process Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Development Process</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We follow a proven methodology that ensures quality, transparency, and timely delivery of your project
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-[#00d690]"></div>
              
              {developmentProcessSteps.map((step, index) => (
                <div key={index} className="relative flex items-start mb-12">
                  {/* Timeline Dot */}
                  <div className="absolute left-6 w-4 h-4 bg-[#00d690] rounded-full border-4 border-white shadow-lg"></div>
                  
                  {/* Content */}
                  <div className="ml-16 bg-gray-50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center text-xl mr-4">
                        {step.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">{step.phase}</h3>
                        <p className="text-[#00d690] font-semibold">{step.duration}</p>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-4">{step.description}</p>
                    
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Key Deliverables:</h4>
                      <div className="flex flex-wrap gap-2">
                        {step.deliverables.map((deliverable, idx) => (
                          <span key={idx} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                            {deliverable}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Share Your Requirements Section */}
      <section id="share-your-requirements" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-800 mb-6 animate-fade-in-up">
              Share Your Requirements
            </h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 mb-12 animate-fade-in-up animation-delay-200">
              Tell us about your project and let our expert team create the perfect solution for your business needs.
            </p>
            
            <div className="bg-gray-50 p-8 rounded-xl shadow-lg">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <input 
                      type="text" 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00d690] focus:border-transparent"
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00d690] focus:border-transparent"
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00d690] focus:border-transparent"
                    placeholder="Your phone number"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Type</label>
                  <select 
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00d690] focus:border-transparent"
                    required
                  >
                    <option value="">Select project type</option>
                    <option value="Web Development">Web Development</option>
                    <option value="Mobile App Development">Mobile App Development</option>
                    <option value="AI Integration">AI Integration</option>
                    <option value="Cloud & DevOps">Cloud & DevOps</option>
                    <option value="UI/UX Design">UI/UX Design</option>
                    <option value="Backend Development">Backend Development</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Description</label>
                  <textarea 
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00d690] focus:border-transparent"
                    placeholder="Describe your project requirements, goals, and timeline..."
                    required
                  ></textarea>
                </div>
                
                {submitMessage && (
                  <div className={`p-4 rounded-lg text-center ${
                    submitMessage.includes('Thank you') 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-red-100 text-red-700 border border-red-200'
                  }`}>
                    {submitMessage}
                  </div>
                )}
                
                <div className="text-center">
                  <Button 
                    type="submit"
                    variant="primary" 
                    className="px-8 py-3 text-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Requirements'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Hire Pre-Vetted Developers Section */}
      <section id="pricing" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Hire Pre-Vetted Developers in 48 Hours
            </h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get access to our pool of experienced, pre-vetted developers ready to join your project within 48 hours.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div 
                key={index} 
                className={`bg-white p-8 rounded-xl shadow-lg relative ${
                  plan.popular ? 'ring-2 ring-[#00d690]' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#00d690] text-white px-4 py-2 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="text-center mb-8">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{plan.title}</h3>
                  <div className="text-4xl font-bold text-[#00d690] mb-2">{plan.price}</div>
                  <div className="text-gray-600 mb-4">{plan.period}</div>
                  <p className="text-gray-600">{plan.description}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <Icon name="Check" size={16} className="text-[#00d690] mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  variant={plan.popular ? "primary" : "outline"}
                  className="w-full"
                  onClick={() => navigate('/contact')}
                >
                  Get Started
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calendly Meeting Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Schedule a Free Consultation
            </h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Book a 30-minute consultation with our experts to discuss your project requirements and get a personalized quote.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            {calendlyError ? (
              <div className="text-center py-12 md:py-20 bg-white rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="Calendar" size={32} className="text-[#00d690]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Schedule a Consultation</h3>
                <p className="text-gray-600 mb-6">
                  We're having trouble loading our calendar widget. Please contact us directly to schedule a consultation.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center justify-center space-x-4">
                    <Icon name="Mail" size={20} className="text-[#00d690]" />
                    <a href="mailto:business@octalchip.com" className="text-[#00d690] hover:underline">
                      business@octalchip.com
                    </a>
                  </div>
                  <div className="flex items-center justify-center space-x-4">
                    <Icon name="Phone" size={20} className="text-[#00d690]" />
                    <a href="tel:+917574082582" className="text-[#00d690] hover:underline">
                      +91-7574082582
                    </a>
                  </div>
                </div>
                <div className="mt-8">
                  <Button 
                    variant="primary"
                    onClick={() => window.open('https://calendly.com/business-octalchip/30min', '_blank')}
                  >
                    Open Calendar in New Tab
                  </Button>
                </div>
              </div>
            ) : isCalendlyLoaded ? (
              <div 
                className="calendly-inline-widget" 
                data-url="https://calendly.com/business-octalchip/30min"
                style={{ 
                  minWidth: '320px', 
                  height: '900px',
                  width: '100%',
                  border: 'none',
                  overflow: 'hidden'
                }}
              ></div>
            ) : (
              <div className="text-center py-12 md:py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00d690] mx-auto mb-4"></div>
                <p className="text-gray-600">Loading calendar...</p>
                <p className="text-sm text-gray-500 mt-2">If the calendar doesn't load, please refresh the page</p>
                <p className="text-xs text-gray-400 mt-2">Debug: isCalendlyLoaded={isCalendlyLoaded.toString()}, calendlyError={calendlyError.toString()}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services; 